import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getUpcomingMatches } from "../services/MatchService";
import { UpcomingMatch } from "../models/UpcomingMatch";
import UpcomingMatchTable from "./UpcomingMatchTable";
import { useLiveMatchLookup } from "../services/LiveService";
import '../styles/buttons.css';

// Create a new mapping for simplified division selection
const simpleDivisionMappings = new Map([
    ["ligaen", "Ligaen"],
    ["1div", "1 Division"],
    ["2div", "2 Division"],
    ["3div", "3 Division"],
    ["4div", "4 Division"],
    ["5div", "5 Division"]
]);

interface UpcomingMatchesProps {
    onNoMatches?: () => void;
    /** Opens the Live tab — offered when every match of the chosen day has started. */
    onShowLive?: () => void;
}

const UpcomingMatches: React.FC<UpcomingMatchesProps> = ({ onNoMatches, onShowLive }) => {
    const { t } = useTranslation(['pages', 'common']);
    const [matchesByDate, setMatchesByDate] = useState<{ [date: string]: UpcomingMatch[] }>({});
    const [matchesByDivision, setMatchesByDivision] = useState<{ [division: string]: UpcomingMatch[] }>({});
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedDivision, setSelectedDivision] = useState<string>("all");
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isLoaded, setIsLoaded] = useState(false);
    // A match that has started (or just finished) has moved on to the Live/Resultater tabs.
    const liveMatches = useLiveMatchLookup();

    // Keep track of window resizing to handle mobile layout changes
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchData = async () => {
        try {
            const newMatches = await getUpcomingMatches();
            if (!newMatches || newMatches.length === 0) {
                setIsLoaded(true);
                onNoMatches?.();
                return;
            }
    
            const matchesByDateTemp: { [date: string]: UpcomingMatch[] } = {};
            const matchesByDivisionTemp: { [division: string]: UpcomingMatch[] } = {};
    
            newMatches.forEach(match => {
                const matchDate = match.match_time.split('T')[0]; // Extract date part
                matchesByDateTemp[matchDate] = matchesByDateTemp[matchDate] || [];
                matchesByDateTemp[matchDate].push(match);
    
                const divisions = new Set<string>();
                if (match.team1?.division) divisions.add(match.team1.division);
                if (match.team2?.division) divisions.add(match.team2.division);
    
                divisions.forEach(division => {
                    let simplifiedDivision;
    
                    // Special case for Ligaen
                    if (division.toLowerCase() === "ligaen") {
                        simplifiedDivision = "ligaen";
                    } else {
                        // Group numeric divisions without specific groups (like "1divB", "3divD")
                        simplifiedDivision = division.match(/^(\ddiv)/)?.[1] || division.match(/^(\d+)/)?.[1] + "div" || division;
                    }
    
                    matchesByDivisionTemp[simplifiedDivision] = matchesByDivisionTemp[simplifiedDivision] || [];
                    matchesByDivisionTemp[simplifiedDivision].push(match);
                });
            });
    
            setMatchesByDate(matchesByDateTemp);
            setMatchesByDivision(matchesByDivisionTemp);
            setIsLoaded(true);
        } catch (error) {
            console.error('Error fetching data:', error);
            setIsLoaded(true);
        }
    };

    const getLatestDateFromDict = useCallback((dict: { [date: string]: UpcomingMatch[] }) => {
        const dates = Object.keys(dict);
        if (dates.length === 0) {
            return null;
        }

        const latestDate = dates.reduce((latest, date) => {
            return latest < date ? latest : date;
        });

        setSelectedDate(latestDate);
    }, []);

    useEffect(() => {
        getLatestDateFromDict(matchesByDate);
    }, [matchesByDate, getLatestDateFromDict]);

    useEffect(() => {
        fetchData();
    }, []);

    if (!isLoaded) {
        return <p className="text-center">{t('common:loading')}</p>;
    }

    if (Object.keys(matchesByDate).length === 0) {
        return <p className="text-center">{t('pages:matches.noUpcoming')}</p>;
    }

    return (
        <div>
            <div className="text-center mb-3">
                <div className={`dropdown d-inline-block ${isMobile ? 'w-100 mb-2' : 'me-2'}`}>
                    <button 
                        className={`btn btn-dropdown dropdown-toggle ${isMobile ? 'w-100' : ''}`} 
                        type="button" 
                        data-bs-toggle="dropdown" 
                        aria-expanded="false"
                        style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            color: 'rgba(255, 255, 255, 0.9)',
                            border: '1px solid rgba(255, 102, 0, 0.2)',
                            borderRadius: '6px',
                            padding: '8px 16px',
                            fontWeight: '500',
                            transition: 'background-color 0.2s ease, color 0.2s ease, padding-left 0.2s ease'
                        }}
                    >
                        {selectedDate ? selectedDate : t('pages:matches.selectDate')}
                    </button>
                    <ul className={`dropdown-menu ${isMobile ? 'w-100' : ''}`}
                        style={{
                            background: 'rgba(26, 26, 26, 0.95)',
                            border: '1px solid rgba(255, 102, 0, 0.2)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                            backdropFilter: 'blur(10px)'
                        }}>
                        {
                            Object.keys(matchesByDate).map((date, index) => (
                                <li key={index}>
                                    <button className="dropdown-item" 
                                        onClick={() => setSelectedDate(date)}
                                        style={{
                                            color: 'rgba(255, 255, 255, 0.8)',
                                            transition: 'background-color 0.2s ease, color 0.2s ease, padding-left 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 102, 0, 0.1)';
                                            e.currentTarget.style.color = '#ff9933';
                                            e.currentTarget.style.paddingLeft = '20px';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                            e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
                                            e.currentTarget.style.paddingLeft = '12px';
                                        }}>
                                        {date}
                                    </button>
                                </li>
                            ))
                        }
                    </ul>
                </div>
                <div className={`dropdown d-inline-block ${isMobile ? 'w-100' : ''}`}>
                    <button 
                        className={`btn btn-dropdown dropdown-toggle ${isMobile ? 'w-100' : ''}`} 
                        type="button" 
                        data-bs-toggle="dropdown" 
                        aria-expanded="false"
                        style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            color: 'rgba(255, 255, 255, 0.9)',
                            border: '1px solid rgba(255, 102, 0, 0.2)',
                            borderRadius: '6px',
                            padding: '8px 16px',
                            fontWeight: '500',
                            transition: 'background-color 0.2s ease, color 0.2s ease, padding-left 0.2s ease'
                        }}
                    >
                        {selectedDivision === "all" ? t('pages:matches.allDivisions') : simpleDivisionMappings.get(selectedDivision)}
                    </button>
                    <ul className={`dropdown-menu ${isMobile ? 'w-100' : ''}`}
                        style={{
                            background: 'rgba(26, 26, 26, 0.95)',
                            border: '1px solid rgba(255, 102, 0, 0.2)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                            backdropFilter: 'blur(10px)'
                        }}>
                        <li>
                            <button className="dropdown-item" 
                                onClick={() => setSelectedDivision("all")}
                                style={{
                                    color: 'rgba(255, 255, 255, 0.8)',
                                    transition: 'background-color 0.2s ease, color 0.2s ease, padding-left 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 102, 0, 0.1)';
                                    e.currentTarget.style.color = '#ff9933';
                                    e.currentTarget.style.paddingLeft = '20px';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'transparent';
                                    e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
                                    e.currentTarget.style.paddingLeft = '12px';
                                }}>
                                {t('pages:matches.allDivisions')}
                            </button>
                        </li>
                        {
                            Array.from(simpleDivisionMappings.keys()).map((division, index) => (
                                <li key={index}>
                                    <button className="dropdown-item" 
                                        onClick={() => setSelectedDivision(division)}
                                        style={{
                                            color: 'rgba(255, 255, 255, 0.8)',
                                            transition: 'background-color 0.2s ease, color 0.2s ease, padding-left 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 102, 0, 0.1)';
                                            e.currentTarget.style.color = '#ff9933';
                                            e.currentTarget.style.paddingLeft = '20px';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                            e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
                                            e.currentTarget.style.paddingLeft = '12px';
                                        }}>
                                        {simpleDivisionMappings.get(division)}
                                    </button>
                                </li>
                            ))
                        }
                    </ul>
                </div>
            </div>
            {
                selectedDate && (() => {
                    const forDate = (selectedDivision === "all" ? matchesByDate[selectedDate] :
                        matchesByDivision[selectedDivision]?.filter(match => match.match_time.split('T')[0] === selectedDate)) || [];
                    const notStarted = forDate.filter(match => !liveMatches[match.challonge_id]);
                    if (forDate.length > 0 && notStarted.length === 0) {
                        return (
                            <p className="text-center">
                                {t('pages:live.allStarted')}{' '}
                                {onShowLive && (
                                    <button type="button" className="btn btn-link p-0 align-baseline" style={{ color: '#f15c25', fontWeight: 600 }} onClick={onShowLive}>
                                        {t('pages:live.seeLive')} →
                                    </button>
                                )}
                            </p>
                        );
                    }
                    return <UpcomingMatchTable matchesForDate={notStarted} isMobile={isMobile} />;
                })()
            }
        </div>
    );
}

export default UpcomingMatches;